import React, { useEffect, useState } from 'react';
import {
    Box, CircularProgress, Grid, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Chip, Pagination, IconButton, Tooltip
} from '@mui/material';
import { paginationStyle } from '../../theme/formStyles';
import SearchBox from '../Common/SearchBox';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { getAllEquipmentMeters } from '../../Redux/actions';
import { updateParams } from '../../utils';
import Filter from '../Common/Filter';

const tableColumns = ['Sl.No.', 'Meter Name', 'Meter ID', 'Client', 'Facility', 'Projects', 'Status'];
const itemsPerPage = 20;

export default function EquipmentMeterList() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [queryParams] = useSearchParams();

    const [responseData, setResponseData] = useState([]);
    const [showLoader, setShowLoader] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [showFilterDialog, setShowFilterDialog] = useState(false);

    const searchValue = queryParams.get('search') || '';

    const updateQueryParams = (params) => {
        const existingParams = Object.fromEntries([...queryParams]);
        const searchParams = updateParams(existingParams, params);
        navigate({ search: searchParams.toString() }, { replace: true });
    };

    useEffect(() => {
        const existingParams = Object.fromEntries([...queryParams]);
        const params = {
            page: parseInt(existingParams.page || '1', 10),
            title: existingParams.search || undefined,
            client_id: existingParams.client_id || undefined,
            facility_id: existingParams.facility_id || undefined,
            project_id: existingParams.project_id || undefined,
            meterStatus: existingParams.meterStatus || undefined,
        };
        setShowLoader(true);
        dispatch(getAllEquipmentMeters(params)).then((res) => {
            if (res && res.status === 200) {
                setResponseData(res.data.data || []);
                setTotalPages(res.data.totalPages);
                setTotalCount(res.data.totalItems);
                setCurrentPage(res.data.currentPage);
            }
            setShowLoader(false);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryParams]);

    const handleSearch = (value) => {
        updateQueryParams({ search: value, page: 1 });
    };

    const handlePageChange = (_, newPage) => {
        updateQueryParams({ page: newPage });
    };

    let tableContent;
    if (showLoader) {
        tableContent = (
            <TableRow>
                <TableCell colSpan={7} align="center"><CircularProgress /></TableCell>
            </TableRow>
        );
    } else if (responseData.length === 0) {
        tableContent = (
            <TableRow>
                <TableCell colSpan={7} align="center">
                    <h5 className="w3-text-grey">No Data Found</h5>
                </TableCell>
            </TableRow>
        );
    } else {
        tableContent = responseData.map((item, index) => {
            const projectNames = [...new Set(
                (item.projectMeter || []).map(pm => pm?.project?.short_name).filter(Boolean)
            )];
            const facilityNames = [...new Set(
                (item.projectMeter || []).map(pm => pm?.facilities?.facility_name).filter(Boolean)
            )];
            return (
                <TableRow key={item.mid}>
                    <TableCell>{(currentPage - 1) * itemsPerPage + index + 1}</TableCell>
                    <TableCell>{item.metername}</TableCell>
                    <TableCell>{item.meterid}</TableCell>
                    <TableCell>{item.client_id}</TableCell>
                    <TableCell>
                        <Box display="flex" flexDirection="column" gap={0.5}>
                            {facilityNames.map(f => (
                                <Tooltip key={f} title={f} arrow>
                                    <Box sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'default', fontSize: '13px' }}>
                                        {f}
                                    </Box>
                                </Tooltip>
                            ))}
                        </Box>
                    </TableCell>
                    <TableCell>
                        <Box display="flex" flexDirection="column" gap={0.5}>
                            {projectNames.map(p => (
                                <Tooltip key={p} title={p} arrow>
                                    <Box sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'default', fontSize: '13px' }}>
                                        {p}
                                    </Box>
                                </Tooltip>
                            ))}
                        </Box>
                    </TableCell>
                    <TableCell>
                        <Chip
                            size="small"
                            label={item.status || 'N/A'}
                            style={{ background: item.status === 'ACTIVE' ? '#008080' : '#9e9e9e', color: 'white' }}
                        />
                    </TableCell>
                </TableRow>
            );
        });
    }

    return (
        <Grid container>
            <Grid item md={12}>
                <Grid container justifyContent="flex-end" alignItems="center" paddingY={2} gap={1}>
                    <h6 style={{ margin: 0, fontSize: '15px' }}>Search By:</h6>
                    <SearchBox
                        value={searchValue}
                        placeholder="Meter Name / ID"
                        search={handleSearch}
                    />
                    <Tooltip title="Apply Filter">
                        <IconButton onClick={() => setShowFilterDialog(true)}>
                            <FilterListIcon />
                        </IconButton>
                    </Tooltip>
                </Grid>
                <Filter
                    filterArea="EquipmentMeters"
                    showFilterDialog={showFilterDialog}
                    setShowFilterDialog={setShowFilterDialog}
                    updateQueryParams={updateQueryParams}
                    queryParams={queryParams}
                    setIsSearchOrFilterApplied={() => {}}
                />

                <Grid paddingY={1}>
                    <TableContainer component={Paper}>
                        <Table aria-label="equipment meters table" style={{ tableLayout: 'auto' }}>
                            <TableHead>
                                <TableRow>
                                    {tableColumns.map(col => (
                                        <TableCell key={col} style={{ fontSize: '14px', fontWeight: 'bold' }}>{col}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>{tableContent}</TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>

            {responseData.length > 0 && (
                <Grid container item xs={12} style={{ display: 'flex', justifyContent: 'center' }}>
                    <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end', paddingRight: '24px' }}>
                        <span style={{ fontFamily: '"Helvetica Neue"', fontSize: '13px', color: '#3d3d3d' }}>
                            {Math.min((currentPage - 1) * itemsPerPage + 1, totalCount)}-
                            {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount}
                        </span>
                    </Grid>
                    <Pagination
                        page={currentPage}
                        count={totalPages}
                        variant="outlined"
                        shape="rounded"
                        sx={paginationStyle}
                        onChange={handlePageChange}
                    />
                </Grid>
            )}
        </Grid>
    );
}
